import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy } from '@angular/core';
import { GridDetails } from '../../sample-tracking/tracking-overview/interfaces/grid';
import { Subscription, merge, of } from 'rxjs';
import { FormControl } from '@angular/forms';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { SelectData, SelectItem } from '../../sample-tracking/reports/interfaces/filter-data';
import { SiteShippingToolStorage } from '../service/site-shipping-tool-storage';
import { DateRangeFilterValue } from '../../shared/time-frame-filter/timeframe-filter.component';
import * as moment from 'moment';
import { SampleColumnsService } from '../../sample-tracking/filter-samples/column/sample-column-service';
import { ProtocolGridData, ProtocolShipmentService } from '../service/protocol-shipment-list.service';
import { ActivityLogger } from '../../activities/activity-logger';
import { CourierModelDialogService } from '../../shared/courier-model/courier-model-dialog-service';
import { UrlBuilder } from '../../core';

@Component({
    selector: 'protocol-shipment-list',
    template: require('./protocol-shipment-list.component.html'),
    styles: [require('./protocol-shipment-list.component.scss')],
    changeDetection: ChangeDetectionStrategy.OnPush
})

export class ProtocolShipmentListComponent implements OnDestroy {

    public gridDetails: GridDetails;
    public isLoadingCompleted = true;
    public isProtocolLoading = true;
    public protocolError: string = null;
    public isSubmitted = false;
    public pagePerItems = [25, 50, 75, 100, 250, 500];
    public defaultPageSize = 25;
    public searchTerm = new FormControl('');
    public protocolDefaultText = { name: 'Select Protocol', id: 0 };
    public protocolData = { isSingleSelect: true, title: 'Samples Shipped For:' } as SelectData;
    private searchSubscription: Subscription;
    private timeFilterData: DateRangeFilterValue = { dateBegin: moment().subtract(30, 'days').format('YYYY-MM-DD'), dateEnd: moment().format('YYYY-MM-DD'), rangeType: 'monthly' };
    private selectedProtocol: SelectItem[];
    private protocolShipmentData: any;
    constructor(
        private changeDetectorRef: ChangeDetectorRef,
        private shippingStorage: SiteShippingToolStorage,
        private sampleColumnService: SampleColumnsService,
        private protocolShipmentService: ProtocolShipmentService,
        private activityLogger: ActivityLogger,
        private courierService: CourierModelDialogService,
        private urlBuilder: UrlBuilder,
    ) {
        this.getProtocolList();
        this.gridDetails = { header: this.protocolShipmentService.gridHeader(), data: [] };

        const searchTermFlow = merge(
            of(''),
            this.searchTerm.valueChanges
                .pipe(
                    debounceTime(300),
                    distinctUntilChanged()
                )
        );

        this.searchSubscription = searchTermFlow.subscribe((term) => {
            this.applyGridSearch(term);
        });
    }

    public getProtocolList() {
        this.activityLogger.logActivity('Shipments By Protocol - List View');
        this.shippingStorage.getProtocolList().subscribe(res => {
            this.isProtocolLoading = false;
            this.protocolData = {
                ...this.protocolData, listItems: res.map(r => {
                    return { name: r.protocolCode, id: r.sponsorName, data: r };
                }).sort((a, b) => a.name.localeCompare(b.name))
            };
            this.changeDetectorRef.markForCheck();
        });
    }

    public getSumbittedDataByProtocolList() {
        if (this.selectedProtocol && this.selectedProtocol.length) {
            this.isLoadingCompleted = false;
            const request = {
                fromDateRange: this.timeFilterData.dateBegin,
                toDateRange: this.timeFilterData.dateEnd,
                ...this.selectedProtocol[0].data
            };
            this.shippingStorage.getSumbittedDataByProtocolList(request).subscribe((res: { protocolShipmentData: ProtocolGridData[] }) => {
                res.protocolShipmentData = (res.protocolShipmentData || []).map(ps => {
                    ps.containerReceiptDate = ps.containerReceiptDate && moment(ps.containerReceiptDate).format('DD-MMM-YYYY');
                    ps.collectionDate = ps.collectionDate && moment(ps.collectionDate).format('DD-MMM-YYYY');
                    ps.kitReceiptDate = ps.kitReceiptDate && moment(ps.kitReceiptDate).format('DD-MMM-YYYY');
                    ps.shipmentSubmittedDate = ps.shipmentSubmittedDate && moment(ps.shipmentSubmittedDate).format('DD-MMM-YYYY');
                    return ps;
                });
                this.protocolShipmentData = res.protocolShipmentData;
                this.isLoadingCompleted = true;
                this.applyGridSearch(null);
            });
        } else {
            this.protocolError = 'Please Select Protocol.';
        }
    }

    public timeFrameFilterChange(event: DateRangeFilterValue) {
        this.timeFilterData = event;
        this.getSumbittedDataByProtocolList();
    }

    public onProtocolChange(event: SelectItem[]) {
        this.selectedProtocol = event;
        this.protocolError = null;
        this.getSumbittedDataByProtocolList();
    }

    public exportGrid(isExcelFormat: boolean) {
        this.activityLogger.logActivity(isExcelFormat ? 'Shipments By Protocol - Action : Export Excel' : 'Shipments By Protocol - Action : Export CSV');
        const protocolName = this.selectedProtocol && this.selectedProtocol.find(sp => sp.name) || { name: 'DEFAULT_NAME' };
        this.protocolShipmentService.exportData(
            this.gridDetails.header,
            this.gridDetails.data,
            protocolName.name,
            isExcelFormat,
            `Protocol Code : ${protocolName.name}`,
            this.timeFilterData);
    }

    public onGridItemClick(event: any) {
        this.activityLogger.logActivity('Shipments By Protocol - Action : View Shipment');
        if (event && event.header.key === 'shipmentStatus') {
            this.courierService.showCourierDetailsDialog(event.data.lgxCourierShipmentId, event.data.shipmentStatus, null);
            return;
        }
        this.shipmentSummary(event.data.shipmentId);
    }

    public shipmentSummary(id: number) {
        this.urlBuilder.esa().sampleManagement().siteShipping().shipmentSummary(id).navigate();
    }

    public ngOnDestroy(): void {
        if (this.searchSubscription) {
            this.searchSubscription.unsubscribe();
        }
    }

    public onPagePerItemsChange($event: any) {
        this.defaultPageSize = $event.pageSize;
    }

    private applyGridSearch(term: string) {
        let filteredData = this.sampleColumnService.applySearch(term, this.protocolShipmentData, this.protocolShipmentService.gridHeader());
        this.gridDetails = { header: this.protocolShipmentService.gridHeader(), data: filteredData };
        this.changeDetectorRef.markForCheck();
    }
}